import React, { useCallback, useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import "../financingplus/financingplus.css";

const ASSET = "/assets/financingplus";
const LOGO_WHITE = `${ASSET}/logos/goodleap-white.svg`;
const HERO_BG = `${ASSET}/backgrounds/hero-composite.png`;
const STORE_KEY = "goodleap-paylink-qr-v1";

const WHERE_TO_USE = [
  "Printed estimates and proposals handed over on site",
  "Brochures, flyers and leave-behinds",
  "Yard signs at job sites",
  "Vehicle wraps and door magnets",
  "Slides shown during a consultation",
];

const BENEFITS = [
  "Easily accept online payments with digital invoices and payment links",
  "Seamlessly add recurring billing for service and maintenance plans",
];

function normalizePayLink(link) {
  let next = String(link || "").trim();
  if (next && !/^https?:\/\//i.test(next)) next = `https://${next}`;
  return next;
}

function isValidHttpUrl(value) {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

function CheckIcon() {
  return (
    <span
      aria-hidden
      style={{
        width: 22,
        height: 22,
        borderRadius: 6,
        background: "var(--gl-orange)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        marginTop: 2,
      }}
    >
      <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
        <path
          d="M1.5 5.2L4.4 8.1L10.5 1.5"
          stroke="#14140F"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function ArrowChip() {
  return (
    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" aria-hidden="true" style={{ display: "block" }}>
      <path d="M3.5 10.5H19.25" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M12.133 3.813L18.818 10.498L12.133 17.183"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
    </svg>
  );
}

/**
 * Marketing / toolkit home for pay.goodleap.link (and /paylink on goodleap.dev).
 * Visual language shared with Financing+ (Passion / Grape tokens).
 */
export default function PayLinkHome() {
  const [linkInput, setLinkInput] = useState(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(STORE_KEY) || "null");
      return saved?.link || "";
    } catch {
      return "";
    }
  });
  const [appliedLink, setAppliedLink] = useState("");
  const [qrSvg, setQrSvg] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const qrSvgHref = useMemo(() => {
    if (!qrSvg) return "#";
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qrSvg)}`;
  }, [qrSvg]);

  const generateQr = useCallback(async (rawLink) => {
    const url = normalizePayLink(rawLink ?? linkInput);
    if (!url || !isValidHttpUrl(url)) {
      setError("Enter a valid PayLink URL, e.g. pay.goodleap.link/your-link");
      setQrSvg("");
      setAppliedLink("");
      return;
    }
    setGenerating(true);
    setError("");
    try {
      const svg = await QRCode.toString(url, {
        type: "svg",
        width: 280,
        margin: 2,
        color: { dark: "#200F51", light: "#FFFFFF" },
      });
      setQrSvg(svg);
      setAppliedLink(url);
      try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify({ link: url }));
      } catch {
        /* ignore */
      }
    } catch {
      setError("Could not generate the QR code. Try again.");
      setQrSvg("");
      setAppliedLink("");
    } finally {
      setGenerating(false);
    }
  }, [linkInput]);

  useEffect(() => {
    if (!linkInput) return;
    const url = normalizePayLink(linkInput);
    if (isValidHttpUrl(url)) void generateQr(url);
    // Restore saved QR once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDownloadPng = useCallback(async () => {
    if (!appliedLink || !qrSvg) {
      setError("Generate a QR code first.");
      return;
    }
    try {
      const dataUrl = await QRCode.toDataURL(appliedLink, {
        width: 1024,
        margin: 2,
        color: { dark: "#200F51", light: "#FFFFFF" },
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "paylink-qr.png";
      a.click();
    } catch {
      setError("Could not download the PNG. Try again.");
    }
  }, [appliedLink, qrSvg]);

  const onDownloadSvg = useCallback(
    (e) => {
      if (!qrSvg) {
        e.preventDefault();
        setError("Generate a QR code first.");
      }
    },
    [qrSvg]
  );

  const pageGradient =
    "linear-gradient(93.207deg, #200F51 0%, #200F51 50%, #472BA4 160%)";

  return (
    <div
      className="fp-toolkit"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: pageGradient,
      }}
    >
      <div style={{ flex: "1 0 auto", background: pageGradient }}>
      {/* Dark header (Financing+ style; no setup form) */}
      <div
        style={{
          background: `url('${HERO_BG}') top center/100% auto no-repeat, linear-gradient(93.207deg,#472BA4 -14%,#200F51 88%)`,
        }}
      >
        <div
          style={{
            padding: "18px 48px",
            display: "flex",
            alignItems: "center",
            gap: 28,
            flexWrap: "wrap",
          }}
        >
          <img
            src={LOGO_WHITE}
            alt="GoodLeap"
            style={{ height: 38, width: "auto", display: "block" }}
          />
          <span
            style={{
              width: 1,
              height: 32,
              background: "rgba(255,255,255,0.18)",
              display: "block",
            }}
          />
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 500,
              fontSize: 20,
              lineHeight: 1,
              color: "var(--gl-warm-white)",
            }}
          >
            PayLink
          </span>
        </div>
      </div>

      {/* First section — headline + benefits (replaces Financing+ form) */}
      <section style={{ background: "var(--gl-warm-white)" }}>
        <div
          style={{
            maxWidth: 960,
            margin: "0 auto",
            padding: "80px 48px",
            textAlign: "center",
          }}
        >
          <p className="gl-eyebrow" style={{ color: "var(--gl-passion)", marginBottom: 20 }}>
            GoodLeap PayLink
          </p>
          <div className="gl-title-gradient">
            <h1
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 500,
                fontSize: "clamp(32px,4vw,48px)",
                lineHeight: 1.12,
                margin: "0 0 20px",
              }}
            >
              Get paid anywhere, anytime, any way
            </h1>
          </div>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 18,
              lineHeight: "28px",
              margin: "0 auto 40px",
              maxWidth: 720,
              color: "var(--gl-passion)",
            }}
          >
            Collect payments in the field or from the office with a powerful, all-in-one
            solution built for contractors on the GoodLeap platform.
          </p>

          <div
            style={{
              background: "var(--gl-blush)",
              borderRadius: 24,
              padding: "28px 32px",
              textAlign: "left",
              maxWidth: 720,
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              gap: 18,
            }}
          >
            {BENEFITS.map((line) => (
              <div
                key={line}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 14,
                  fontFamily: "var(--font-body)",
                  fontSize: 17,
                  lineHeight: "26px",
                  color: "var(--gl-passion)",
                }}
              >
                <CheckIcon />
                <span>{line}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QR code section */}
      <section
        className="gl-dark-ground"
        style={{
          background: pageGradient,
          padding: "80px 48px 96px",
        }}
      >
        <div style={{ maxWidth: 1120, margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 500,
              fontSize: "clamp(32px,4vw,48px)",
              lineHeight: 1.1,
              margin: "0 0 12px",
              color: "var(--gl-warm-white)",
            }}
          >
            QR code
          </h2>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 18,
              lineHeight: "26px",
              margin: "0 0 32px",
              color: "var(--gl-lilac)",
            }}
          >
            Generated from your PayLink, available in vector for print and raster for screens.
          </p>

          <div style={{ marginBottom: 28 }}>
            <span
              style={{
                display: "block",
                fontFamily: "var(--font-display)",
                fontWeight: 500,
                fontSize: 12,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--gl-lilac)",
                marginBottom: 8,
              }}
            >
              Your PayLink
            </span>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "center",
                gap: 12,
                width: "100%",
              }}
            >
              <input
                className="fp-setup-input"
                type="url"
                value={linkInput}
                onChange={(e) => {
                  setLinkInput(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void generateQr();
                  }
                }}
                placeholder="Enter Your PayLink Link e.g. pay.goodleap.link/your-link"
                style={{
                  flex: "1 1 auto",
                  minWidth: 0,
                  height: 52,
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.08)",
                  color: "var(--gl-warm-white)",
                  padding: "0 16px",
                  fontFamily: "var(--font-body)",
                  fontSize: 16,
                  boxSizing: "border-box",
                }}
              />
              <button
                type="button"
                className="fp-generate"
                disabled={generating || !linkInput.trim()}
                onClick={() => void generateQr()}
                style={{ height: 52, flex: "0 0 auto", alignSelf: "center" }}
              >
                <span>{generating ? "Generating…" : "Generate"}</span>
                <span className="fp-generate__arrow">
                  <ArrowChip />
                </span>
              </button>
            </div>
          </div>
          {error ? (
            <p
              style={{
                fontFamily: "var(--font-body)",
                fontSize: 14,
                color: "#FFB4A8",
                margin: "0 0 20px",
              }}
            >
              {error}
            </p>
          ) : null}

          <div
            style={{
              background: "var(--gl-white)",
              borderRadius: 24,
              overflow: "hidden",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
              boxShadow: "var(--shadow-card)",
            }}
          >
            <div style={{ padding: 40 }}>
              <h3
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 500,
                  fontSize: 24,
                  lineHeight: "28px",
                  margin: "0 0 16px",
                  color: "var(--gl-passion)",
                }}
              >
                Where to use it
              </h3>
              <ul
                style={{
                  margin: "0 0 24px",
                  paddingLeft: 22,
                  fontFamily: "var(--font-body)",
                  fontSize: 16,
                  lineHeight: "26px",
                  color: "var(--gl-warm-black)",
                }}
              >
                {WHERE_TO_USE.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 14,
                  lineHeight: "20px",
                  margin: 0,
                  color: "var(--text-muted)",
                }}
              >
                This code points at the PayLink you enter above — scan it with your own phone
                before it goes to print.
              </p>
            </div>

            <div
              style={{
                background: "var(--gl-light-asphalt)",
                padding: 40,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 24,
              }}
            >
              <div
                style={{
                  width: 280,
                  height: 280,
                  background: "var(--gl-white)",
                  borderRadius: 16,
                  padding: 20,
                  boxShadow: "var(--shadow-raised)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {qrSvg ? (
                  <div
                    style={{ width: "100%", height: "100%" }}
                    dangerouslySetInnerHTML={{
                      __html: qrSvg.replace(
                        "<svg ",
                        '<svg style="width:100%;height:100%;display:block" '
                      ),
                    }}
                  />
                ) : (
                  <p
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: 14,
                      lineHeight: "20px",
                      margin: 0,
                      color: "var(--text-muted)",
                      textAlign: "center",
                      padding: 16,
                    }}
                  >
                    Enter your PayLink and choose Generate to preview the QR code.
                  </p>
                )}
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
                <a
                  href={qrSvg ? qrSvgHref : "#"}
                  download="paylink-qr.svg"
                  onClick={onDownloadSvg}
                  className={`fp-btn fp-btn--primary${!qrSvg ? " fp-btn--disabled" : ""}`}
                  aria-disabled={!qrSvg}
                  style={{ color: "#FFFFFF", WebkitTextFillColor: "#FFFFFF" }}
                >
                  Download SVG
                </a>
                <button
                  type="button"
                  className={`fp-btn fp-btn--secondary${!qrSvg ? " fp-btn--disabled" : ""}`}
                  disabled={!qrSvg}
                  onClick={() => void onDownloadPng()}
                >
                  Download PNG
                </button>
              </div>
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 12,
                  lineHeight: "16px",
                  margin: 0,
                  color: "var(--text-muted)",
                  textAlign: "center",
                }}
              >
                SVG for print, PNG for slides and screens.
              </p>
              {appliedLink ? (
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: 12,
                    lineHeight: "16px",
                    margin: 0,
                    color: "var(--text-muted)",
                    textAlign: "center",
                    wordBreak: "break-all",
                  }}
                >
                  {appliedLink}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </section>
      </div>

      {/* Footer — sticks to bottom when viewport is taller than content */}
      <footer
        className="gl-dark-ground"
        style={{
          flexShrink: 0,
          marginTop: "auto",
          padding: "56px 48px",
          background: "#200F51",
        }}
      >
        <div
          style={{
            maxWidth: 1344,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 40,
            flexWrap: "wrap",
          }}
        >
          <img
            src={LOGO_WHITE}
            alt="GoodLeap"
            style={{ height: 36, width: "auto", display: "block" }}
          />
          <p
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 500,
              fontSize: 24,
              lineHeight: "28px",
              margin: 0,
              color: "var(--gl-warm-white)",
              textAlign: "center",
              flex: "1 1 auto",
            }}
          >
            Built for how you work
          </p>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 400,
              fontSize: 14,
              lineHeight: "20px",
              margin: 0,
              color: "var(--gl-lilac)",
              textAlign: "right",
              whiteSpace: "nowrap",
            }}
          >
            © 2026 GoodLeap. All rights reserved
          </p>
        </div>
      </footer>
    </div>
  );
}
